import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Ability } from '@casl/ability';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Issue, IssueType, Project } from '../../../../../../../shared/models';
import { ProjectService } from '../../../../../../../shared/services';
import { CommonService } from '../../../../../../../shared/services/common.service';
import { IssueService } from '../../../../../../issue/issue.service';
import { ProjectsService } from '../../../../../core/projects.service';
import { FetchIssueTypesRequest } from '../../../../../core/projects.actions';
import { issueTypesFilter, projectDetails } from '../../../../../core/projects.selectors';
import {
  IssueDetailsWrapperComponent,
} from '../../../../site-details-page/tab-screens/daily-report/daily-report-components/detail-components/issue-details/issue-details-wrapper/issue-details-wrapper.component';
import { MultiSectionFilterSection } from '../../filters/multi-section-filter/multi-section-filter.component';
import * as moment from 'moment-timezone';
import {
  DEFAULT_ISSUE_SORT,
  defaultIssueSortDir,
  filterIssues,
  IssueActivity,
  IssueExportFormat,
  issueListActivity,
  issuesTabApiQuery,
  ISSUE_EXPORT_OPTIONS,
  IssueSortDir,
  issueStatusTone,
  ISSUE_SORT_OPTIONS,
  ISSUE_STATUS_ALL,
  ISSUE_TAB_STATUSES,
  sortIssues,
  toCheckboxOptions,
  toParamList,
  withIssueMediaTypes,
} from '../../../../../../../shared/helpers/issues-tab';
import {
  isIssueWorkflowEnabled,
  issueStatusGroup,
  issueStatusLabel,
  issueWorkflowHistory,
} from '../../../../../../../shared/helpers/issue-workflow';

/** Filter statuses of a contractor-workflow project, in workflow order. */
const ISSUE_WORKFLOW_FILTER_STATUSES = ['open', 'sent_to_contractor', 'responded', 'closed'];

/**
 * Project Issues tab (/projects/:projectId?tab=issues). Replaces the
 * standalone /issues/project/:projectId page (which now redirects here) with
 * the RFIs tab's layout: toolbar (date range on created, Filter on status /
 * category / scope, sort, search, Create) over the RFI log table. Every filter
 * lives in the url, so links and reloads keep the view. Rows open the details
 * / create panels the old page used.
 *
 * With no status in the url the tab shows the open issues (every status but
 * closed), written back to the url; `status=all` is the cleared filter.
 */
@Component({
  selector: 'app-project-issues-tab',
  templateUrl: './issues-tab.component.html',
  styleUrls: ['./issues-tab.component.scss'],
  standalone: false,
})
export class IssuesTabComponent implements OnInit, OnDestroy {
  readonly defaultDateValue = 'All time';
  readonly exportOptions = ISSUE_EXPORT_OPTIONS;
  readonly sortOptions = ISSUE_SORT_OPTIONS;
  readonly displayedColumns = ['title', 'site', 'category', 'status', 'createdAt', 'latestActivity'];
  /** Column → the sort it sets (the Sort dropdown's values). */
  readonly columnSort: Record<string, string> = {
    title: 'Title',
    site: 'Site',
    category: 'Category',
    status: 'Status',
    createdAt: 'Created at',
    latestActivity: 'Latest activity',
  };

  projectId: string;
  project: Project = null;
  projectTimezone: string = null;
  canCreate = false;
  loading = false;
  loaded = false;
  /** The export being downloaded: its menu rows are disabled until it lands. */
  exporting: IssueExportFormat | null = null;

  issues: Issue[] = [];
  filteredIssues: Issue[] = [];
  pageIssues: Issue[] = [];
  issueTypes: IssueType[] = [];
  selectedIssueId: string = null;

  statuses: string[] = [];
  categories: string[] = [];
  scopes: string[] = [];
  startDate: string = null;
  endDate: string = null;
  searchText = '';
  sort: string = DEFAULT_ISSUE_SORT;
  sortDir: IssueSortDir = defaultIssueSortDir(DEFAULT_ISSUE_SORT);
  /**
   * Latest workflow history entry per issue id, where it has been fetched;
   * issues without one show what the list itself says (issueListActivity).
   */
  activityById: Record<string, IssueActivity> = {};
  /** No status in the url yet: the default is about to be written there. */
  awaitingDefaultStatus = false;
  filterSections: MultiSectionFilterSection[] = [];

  currentPage = 1;
  readonly itemsPerPage = 10;
  private paginatedTotal = -1;

  form = new FormGroup({
    searchText: new FormControl(''),
  });

  /**
   * The url's selectedId is opened once, after the first fetch: the old page's
   * deep link (global search, emails), kept through the redirect.
   */
  private pendingSelectedId: string = null;
  private filterKey: string = null;
  /** Issue ids whose workflow history has been asked for (see loadActivity). */
  private readonly activityRequested = new Set<string>();
  private readonly onDestroy = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private ability: Ability,
    private issueService: IssueService,
    private projectSrv: ProjectService,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private snackBar: MatSnackBar,
  ) {
  }

  ngOnInit(): void {
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    // Type-level check, like the old page's route guard (s: Issue, a: read):
    // issue rules are conditioned on the scope, which a new issue has not got.
    this.canCreate = this.ability.can('create', 'Issue');
    this.pendingSelectedId = this.route.snapshot.queryParamMap.get('selectedId');

    if (this.projectId) {
      this.store.dispatch(FetchIssueTypesRequest({ payload: { projectId: this.projectId } }));
    }

    this.store.select(issueTypesFilter)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(types => {
        this.issueTypes = (types || []) as IssueType[];
        this.refreshFilterSections();
        this.applyFilters(false);
      });

    this.store.select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        // The store keeps whichever project was fetched last.
        this.project = project?.id === this.projectId ? project : null;
        this.projectTimezone = (this.project as { timezone?: string })?.timezone || null;
        this.refreshFilterSections();
        this.applyFilters(false);
        this.ensureDefaultStatus();
      });

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        this.statuses = toParamList(qp.status);
        this.awaitingDefaultStatus = qp.status === undefined;
        this.categories = toParamList(qp.category);
        this.scopes = toParamList(qp.scope);
        this.startDate = qp.startDate || null;
        this.endDate = qp.endDate || null;
        this.sort = qp.sort === undefined ? DEFAULT_ISSUE_SORT : (qp.sort || null);
        this.sortDir = qp.sortDir === 'asc' || qp.sortDir === 'desc' ? qp.sortDir : defaultIssueSortDir(this.sort);
        this.searchText = qp.search || '';
        this.selectedIssueId = qp.selectedId || null;
        if ((this.form.controls.searchText.value || '') !== this.searchText) {
          this.form.controls.searchText.setValue(this.searchText, { emitEvent: false });
        }
        this.refreshFilterSections();
        // Opening / closing an issue only changes selectedId: keep the page.
        const filterKey = JSON.stringify([
          this.statuses, this.categories, this.scopes, this.startDate, this.endDate, this.sort, this.sortDir,
          this.searchText,
        ]);
        const filtersChanged = filterKey !== this.filterKey;
        this.filterKey = filterKey;
        this.applyFilters(filtersChanged);
        this.ensureDefaultStatus();
      });

    this.form.controls.searchText.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.onDestroy))
      .subscribe(text => {
        this.router.navigate([], {
          queryParams: { search: (text || '').trim() || null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      });

    // Saves / status changes in the details panel ask the issue lists to refetch.
    this.issueService.fetchIssuesList
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.fetchIssues());
    this.projectSrv.fetchIssues
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.fetchIssues());

    this.commonService.clearRowSelection
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => (this.selectedIssueId = null));

    // An issue pin picked on the project map opens like its row.
    this.projectsService.issueMapPinSelected
      .pipe(takeUntil(this.onDestroy))
      .subscribe(pin => {
        if (pin?.id) {
          this.openIssue(this.issues.find(issue => `${issue.id}` === `${pin.id}`) || ({ id: pin.id } as Issue));
        }
      });

    this.fetchIssues();
  }

  fetchIssues(): void {
    if (!this.projectId) {
      return;
    }
    this.loading = true;
    this.issueService
      .fetchRecords(new Project({ id: this.projectId }), { pageIndex: 0, pageSize: 1000 })
      .then(resp => {
        this.issues = resp.result || [];
        // A save / workflow action refetches the list: its history may have moved on.
        this.activityById = {};
        this.activityRequested.clear();
        this.applyFilters(false);
        this.openPendingIssue();
      })
      .catch(err => console.error(err))
      .finally(() => {
        this.loading = false;
        this.loaded = true;
      });
  }

  /**
   * Status values the filter offers: the contractor-workflow statuses when the
   * project has issue_workflow_enabled (Open, Forwarded, Responded, Closed),
   * open / closed otherwise.
   */
  statusChoices(): { value: string; label: string }[] {
    if (isIssueWorkflowEnabled(this.project)) {
      return ISSUE_WORKFLOW_FILTER_STATUSES.map(value => ({ value, label: issueStatusLabel(value) }));
    }
    return ISSUE_TAB_STATUSES;
  }

  /**
   * Folds an issue's status onto the values statusChoices() offers: as is on a
   * workflow project, open / closed (a stage counts as open) otherwise.
   */
  statusKeyOf = (status: string | null | undefined): string =>
    isIssueWorkflowEnabled(this.project) ? (status || '') : issueStatusGroup(status);

  statusLabel = (status: string): string => issueStatusLabel(status);

  categoryLabel = (type: string): string =>
    this.issueTypes.find(t => t?.type === type)?.label || type;

  /** The statuses the tab opens on: every one but closed. */
  defaultStatuses(): string[] {
    return this.statusChoices().map(s => s.value).filter(value => value !== 'closed');
  }

  /**
   * Writes the default status filter into the url when it has none (first
   * visit, a click on the tab). Waits for the project: on a workflow project
   * the open statuses include its stages.
   */
  private ensureDefaultStatus(): void {
    if (!this.project || this.route.snapshot.queryParams.status !== undefined) {
      return;
    }
    this.router.navigate([], {
      queryParams: { status: this.defaultStatuses() },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  refreshFilterSections(): void {
    const sites = (this.project?.sites || []) as any[];
    const scopeChoices = this.project
      ? [
        { value: `${this.project.id}`, label: `Project ${this.project.name}` },
        ...sites.map(site => ({ value: `${site.id}`, label: `Site ${site.name}` })),
      ]
      : [];

    this.filterSections = [
      // All statuses ticked is a real choice here (the default is a subset): it still counts in Filter (N)
      { key: 'status', label: 'Status', countWhenAll: true, options: toCheckboxOptions(this.statusChoices(), this.statuses) },
      {
        key: 'category',
        label: 'Category',
        searchable: this.issueTypes.length > 6,
        options: toCheckboxOptions(
          this.issueTypes.map(t => ({ value: t.type, label: t.label })),
          this.categories,
        ),
      },
      {
        key: 'scope',
        label: 'Scope',
        searchable: scopeChoices.length > 6,
        options: toCheckboxOptions(scopeChoices, this.scopes),
      },
    ];
  }

  onFilterChange(event: { key: string; values: any[] }): void {
    let value: any[] | string | null = event.values?.length ? event.values : null;
    // No status at all would bring the default back: keep "cleared" explicit.
    if (event.key === 'status' && !value) {
      value = ISSUE_STATUS_ALL;
    }
    this.router.navigate([], {
      queryParams: { [event.key]: value },
      queryParamsHandling: 'merge',
    });
  }

  /** Sort dropdown / a new column header: that column, in its default direction. */
  onSortChange(sort: string | null): void {
    this.router.navigate([], {
      // An empty value keeps "no sort" distinct from the default (no param).
      queryParams: { sort: sort === DEFAULT_ISSUE_SORT ? null : (sort ?? ''), sortDir: null },
      queryParamsHandling: 'merge',
    });
  }

  /** Column header click: sorts by the column, or flips the direction when it is the sort already. */
  onHeaderSort(column: string): void {
    const sort = this.columnSort[column];
    if (!sort) {
      return;
    }
    if (sort !== this.sort) {
      this.onSortChange(sort);
      return;
    }
    const dir: IssueSortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    this.router.navigate([], {
      queryParams: { sortDir: dir === defaultIssueSortDir(sort) ? null : dir },
      queryParamsHandling: 'merge',
    });
  }

  isSortedBy(column: string): boolean {
    return !!this.sort && this.columnSort[column] === this.sort;
  }

  latestActivity(issue: Issue): IssueActivity {
    return (issue?.id && this.activityById[issue.id]) || issueListActivity(issue);
  }

  statusTone(status: string): string {
    return issueStatusTone(status);
  }

  siteName(issue: Issue): string {
    if (issue?.entity_type === 'project') {
      return 'Project';
    }
    return (issue?.entity as { name?: string })?.name || '—';
  }

  formatInProjectTimezone(timestamp: unknown, format: string): string {
    if (!timestamp) {
      return '';
    }
    const m = moment.isMoment(timestamp) ? timestamp.clone() : moment.utc(timestamp as any);
    return m.isValid() ? m.tz(this.projectTimezone || moment.tz.guess()).format(format) : '';
  }

  /**
   * Fills activityById for the rows on screen. Only a workflow project's
   * issues have a history (the issue list has no latest_lineage), so only
   * those are fetched: one lineage call per row of the page shown, each issue
   * once until the list is refetched. Until it lands, or when the history is
   * empty, the row shows what the list says.
   */
  protected loadActivity(rows: Issue[]): void {
    if (!isIssueWorkflowEnabled(this.project)) {
      return;
    }
    (rows || [])
      .map(row => row?.id)
      .filter(id => !!id && !this.activityRequested.has(id))
      .forEach(id => {
        this.activityRequested.add(id);
        this.projectsService.fetchIssueWorkflowHistory({ issueId: id })
          .pipe(takeUntil(this.onDestroy))
          .subscribe({
            next: resp => {
              const latest = issueWorkflowHistory(resp?.data)[0];
              if (latest) {
                this.activityById = {
                  ...this.activityById,
                  [id]: { action: latest.action, actor: latest.actor_name, date: latest.date },
                };
              }
            },
            error: () => {
              // No history for this row: it keeps the list's activity.
            },
          });
      });
  }

  /**
   * The tab's filters as the export / for-map query: what the list shows
   * (status, category, scope, created date range in the project's timezone,
   * search) in the order it shows it.
   */
  exportQuery(): Record<string, string> {
    return issuesTabApiQuery(this.projectId, {
      statuses: this.statuses,
      categories: this.categories,
      scopes: this.scopes,
      startDate: this.startDate,
      endDate: this.endDate,
      search: this.searchText,
      sort: this.sort,
      sortDir: this.sortDir,
      timezone: this.projectTimezone,
    });
  }

  /** Downloads the filtered issues as a PDF report, CSV or KMZ (export kebab). */
  exportIssues(format: IssueExportFormat): void {
    if (!this.projectId || this.exporting) {
      return;
    }
    const fallbackName = `${this.project?.name || 'Project'} - Issues.${format}`;
    this.exporting = format;
    this.issueService.exportIssues(format, this.exportQuery(), fallbackName)
      .catch((err: Error) => {
        this.snackBar.open(err?.message || 'Export failed', '', { duration: 5000 });
      })
      .finally(() => {
        this.exporting = null;
      });
  }

  clearSearch(): void {
    this.form.controls.searchText.setValue('');
  }

  applyFilters(resetPage: boolean): void {
    const filtered = filterIssues(this.issues, {
      statuses: this.statuses,
      categories: this.categories,
      scopes: this.scopes,
      search: this.searchText,
      startDate: this.startDate,
      endDate: this.endDate,
      timezone: this.projectTimezone,
      statusKeyOf: this.statusKeyOf,
    }, this.projectId);

    this.filteredIssues = sortIssues(filtered, this.sort, this.sortDir, {
      status: this.statusLabel,
      category: this.categoryLabel,
      activityTime: issue => this.latestActivity(issue).date,
    });

    // app-paginator goes back to page 1 whenever its total changes; follow it.
    if (resetPage || this.filteredIssues.length !== this.paginatedTotal) {
      this.currentPage = 1;
    }
    this.paginatedTotal = this.filteredIssues.length;
    this.paginate();
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.paginate();
  }

  private paginate(): void {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    this.pageIssues = this.filteredIssues.slice(start, start + this.itemsPerPage);
    this.loadActivity(this.pageIssues);
  }

  /**
   * Opens the details panel on the full issue record (GET /v1/issue/:id),
   * falling back to the list row if that fails. Either way the media is
   * tagged for the panel's media section (see withIssueMediaTypes).
   */
  openIssue(issue: Issue): void {
    const id = issue?.id;
    this.selectedIssueId = id;
    this.router.navigate([], {
      queryParams: { selectedId: id || null },
      queryParamsHandling: 'merge',
    });
    const full$ = id ? this.issueService.fetchOne(id).catch(() => issue) : Promise.resolve(issue);
    full$.then(record => {
      // Another row picked (or the panel closed) while this one loaded.
      if (this.selectedIssueId !== id) {
        return;
      }
      this.projectsService.openRightPanel.next({
        component: IssueDetailsWrapperComponent,
        data: { ...withIssueMediaTypes(record || issue) },
      });
    });
  }

  createIssue(): void {
    this.selectedIssueId = null;
    this.router.navigate([], {
      queryParams: { selectedId: null },
      queryParamsHandling: 'merge',
    });
    this.projectsService.openRightPanel.next({
      component: IssueDetailsWrapperComponent,
    });
  }

  /**
   * Opens the url's selectedId once the list is in, or fetches it when it is
   * not listed. Unknown / inaccessible ids leave the list as it is.
   */
  private openPendingIssue(): void {
    const id = this.pendingSelectedId;
    if (!id) {
      return;
    }
    this.pendingSelectedId = null;

    const listed = this.issues.find(issue => `${issue.id}` === `${id}`);
    // openIssue loads the full record itself; an id not in the list is
    // looked up first so a deleted / inaccessible one leaves the list alone.
    const issue$ = listed ? Promise.resolve(listed) : this.issueService.fetchOne(id);
    issue$
      .then(issue => {
        if (issue?.id) {
          this.openIssue(issue);
          // After the paginator has taken the new total (it resets to page 1 then).
          setTimeout(() => this.revealIssue(issue));
        }
      })
      .catch(() => {
        // Deleted or not accessible: stay on the list.
      });
  }

  /** Moves to the page that holds the deep-linked issue. */
  private revealIssue(issue: Issue): void {
    const index = this.filteredIssues.findIndex(i => `${i.id}` === `${issue.id}`);
    if (index >= 0) {
      this.currentPage = Math.floor(index / this.itemsPerPage) + 1;
      this.paginate();
      // Moves app-paginator's own page marker along.
      this.projectsService.changeCurrentPageNumber.next(this.currentPage);
    }
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
